import { useCallback, useEffect, useRef, useState } from 'react';
import type { ContractReplacement, Milestone } from '../types';
import { milestoneApi } from '../services/api';
import { stageVariationOre } from '../utils/stageVariation';

export interface VariationRow {
  key: string;
  id?: string;
  sourceId?: string | null;
  restoresMilestoneId?: string;
  number?: number;
  description: string;
  grossAmount: string;
  amount: string;
  endDate: string;
}
export interface VariationDraft {
  rows: VariationRow[];
  selectedIds: string[];
  grossDelta: string;
}

// Server-assigned IDs and numbers are not user edits. Keep client keys stable
// when new stages receive IDs, so saving does not replace a focused input.
const draftKey = (draft: VariationDraft) =>
  JSON.stringify({
    ...draft,
    rows: draft.rows.map(({ id, number, ...row }) => row)
  });

function bindSavedRows(submitted: VariationDraft, plan: Milestone[]) {
  const submittedIds = new Set(submitted.rows.map((row) => row.id));
  const created = plan.filter(
    (stage) => !stage.replacesMilestoneId && !submittedIds.has(stage.id)
  );
  const newIds = new Map(
    submitted.rows
      .filter((row) => !row.id && !row.sourceId)
      .map((row, index) => [row.key, created[index]])
  );
  return (rows: VariationRow[]) =>
    rows.map((row) => {
      const stage = row.sourceId
        ? plan.find((item) => item.replacesMilestoneId === row.sourceId)
        : row.id
          ? plan.find((item) => item.id === row.id)
          : newIds.get(row.key);
      return {
        ...row,
        id: stage?.id,
        number: stage?.milestoneNumber ?? row.number
      };
    });
}

interface Options {
  contractId: string;
  draft: VariationDraft;
  valid: boolean;
  readOnly: boolean;
  initiallyPending: boolean;
  onBound: (
    bind: (rows: VariationRow[]) => VariationRow[],
    plan: Milestone[]
  ) => void;
  onSaved?: (settlement: ContractReplacement) => Promise<void>;
}

export function useStageVariationAutosave(options: Options) {
  const key = draftKey(options.draft);
  const saved = useRef(options.initiallyPending ? '' : key);
  const failed = useRef<string | null>(null);
  const pendingRefresh = useRef<ContractReplacement | null>(null);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const activeSave = useRef<Promise<boolean> | null>(null);
  const latest = useRef(options);
  latest.current = options;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [revision, setRevision] = useState(0);
  const dirty = saved.current !== key;

  const drain = useCallback(async () => {
    inFlight.current = true;
    try {
      // One request at a time; edits made during a request become the next
      // snapshot, with newly created IDs attached before it is submitted.
      while (true) {
        const current = latest.current;
        const snapshot = current.draft;
        const snapshotKey = draftKey(snapshot);
        if (
          current.readOnly ||
          !current.valid ||
          (snapshotKey === saved.current && !pendingRefresh.current) ||
          snapshotKey === failed.current
        )
          break;
        if (mounted.current) {
          setSaving(true);
          setError(false);
        }
        try {
          if (snapshotKey !== saved.current) {
            const result = await milestoneApi.updateStageVariationPlan(
              current.contractId,
              {
                grossDeltaOre: stageVariationOre(snapshot.grossDelta),
                selectedMilestoneIds: snapshot.selectedIds,
                milestones: snapshot.rows.map((row) => ({
                  ...(row.id ? { id: row.id } : {}),
                  ...(row.sourceId ? { sourceMilestoneId: row.sourceId } : {}),
                  ...(row.restoresMilestoneId
                    ? { restoresMilestoneId: row.restoresMilestoneId }
                    : {}),
                  description: row.description.trim(),
                  grossAmountOre: stageVariationOre(row.grossAmount),
                  amountOre: stageVariationOre(row.amount),
                  endDate: row.endDate || null
                }))
              }
            );
            const plan = result.milestones ?? [];
            const bind = bindSavedRows(snapshot, plan);
            latest.current = {
              ...latest.current,
              draft: {
                ...latest.current.draft,
                rows: bind(latest.current.draft.rows)
              }
            };
            saved.current = snapshotKey;
            pendingRefresh.current = result;
            failed.current = null;
            if (mounted.current) {
              latest.current.onBound(bind, plan);
              setSavedAt(new Date());
            }
          }
          // Refresh the rest of the page only after the current edits have
          // been saved. The editor itself keeps its local state and focus.
          // Retry a failed refresh without creating the same stages again.
          if (
            mounted.current &&
            draftKey(latest.current.draft) === snapshotKey &&
            pendingRefresh.current
          ) {
            await latest.current.onSaved?.(pendingRefresh.current);
            pendingRefresh.current = null;
          }
          if (!mounted.current) pendingRefresh.current = null;
        } catch {
          failed.current = snapshotKey;
          if (mounted.current) setError(true);
          break;
        }
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setSaving(false);
        setRevision((value) => value + 1);
      }
    }
    return !failed.current && latest.current.valid && draftKey(latest.current.draft) === saved.current;
  }, []);

  const flush = useCallback((): Promise<boolean> => {
    if (activeSave.current) return activeSave.current;
    const request = drain();
    activeSave.current = request;
    void request.finally(() => { if (activeSave.current === request) activeSave.current = null; });
    return request;
  }, [drain]);

  useEffect(() => {
    if (
      options.readOnly ||
      !options.valid ||
      !dirty ||
      saving ||
      failed.current === key
    )
      return;
    const timeout = window.setTimeout(() => {
      void flush();
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [key, options.readOnly, options.valid, dirty, saving, revision, flush]);

  useEffect(() => {
    mounted.current = true;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (
        !latest.current.readOnly &&
        (inFlight.current || draftKey(latest.current.draft) !== saved.current)
      ) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      mounted.current = false;
      window.removeEventListener('beforeunload', beforeUnload);
      // A tab/contract switch still submits the last valid edits. An existing
      // request drains its queue without updating the newly selected contract.
      void flush();
    };
  }, [flush]);

  const retry = () => {
    failed.current = null;
    setError(false);
    void flush();
  };
  return { dirty, saving, error, savedAt, flush, retry };
}
